import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError, IMG } from "@/lib/api";
import { compressVideo } from "@/lib/videoCompress";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, X, ArrowLeft, ArrowRight, Check, Lock } from "lucide-react";
import BoostUpsellModal from "@/components/BoostUpsellModal";
import NormalizeReviewModal from "@/components/NormalizeReviewModal";
import AuthGateModal from "@/components/AuthGateModal";
import { fireLeadConversion } from "@/lib/ads";

const AMENITIES = ["parking", "lift", "gym", "pool", "power backup", "security", "wifi", "gas pipeline", "garden", "clubhouse"];
const STEPS = ["Basics", "Location", "Details", "Media"];

const EMPTY = {
  title: "", description: "", listing_type: "rent", property_type: "apartment",
  bhk: 2, bathrooms: 2, area_sqft: 1000, price: 25000,
  city: "", locality: "", address: "", amenities: [], furnishing: "semi",
  available_from: "", image_paths: [], video_url: "", visiting_windows: [],
};

export default function PropertyForm({ mode = "create" }) {
  const nav = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const isEdit = mode === "edit";

  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [form, setForm] = useState(EMPTY);
  const [upsell, setUpsell] = useState(null);   // { id, title } once new listing is created
  const [normalizeTarget, setNormalizeTarget] = useState(null); // { id, title } → AI review before boost
  // Deferred-login flow: File objects held client-side when the user hasn't
  // authed yet. They're uploaded to the server AFTER auth completes at Publish.
  const [pendingFiles, setPendingFiles] = useState([]); // [{ file, previewUrl, key }]
  const [authGateOpen, setAuthGateOpen] = useState(false);
  // Flag: user hit Publish while unauthed. Once auth completes we drain this
  // flag via a useEffect (below) to complete the property post. Using a flag +
  // effect instead of calling submit() straight from the modal's onSuccess
  // avoids a stale-closure bug where the submit() function captured `user=null`
  // at the render before login and would loop back into the auth gate.
  const [pendingSubmit, setPendingSubmit] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/properties/${id}`)
      .then(({ data }) => {
        setForm({
          title: data.title, description: data.description,
          listing_type: data.listing_type, property_type: data.property_type,
          bhk: data.bhk, bathrooms: data.bathrooms, area_sqft: data.area_sqft, price: data.price,
          city: data.city, locality: data.locality, address: data.address,
          amenities: data.amenities || [], furnishing: data.furnishing,
          available_from: data.available_from || "", image_paths: data.image_paths || [],
          video_url: data.video_url || "", visiting_windows: data.visiting_windows || [],
        });
      })
      .catch(() => { toast.error("Property not found"); nav("/dashboard"); })
      .finally(() => setLoading(false));
  }, [id, isEdit, nav]);

  const toggleAmenity = (a) => {
    set("amenities", form.amenities.includes(a) ? form.amenities.filter((x) => x !== a) : [...form.amenities, a]);
  };

  const [uploadProgress, setUploadProgress] = useState(null); // e.g. "3/5"
  const fileInputRef = useRef(null);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // Client-side pre-flight: reject anything that would fail on the server.
    const MAX = 8 * 1024 * 1024;
    const rejected = files.filter((f) => f.size > MAX || !f.type.startsWith("image/"));
    const ok = files.filter((f) => f.size <= MAX && f.type.startsWith("image/"));
    if (rejected.length) {
      toast.error(`${rejected.length} file(s) skipped — must be images under 8MB each`);
    }
    if (!ok.length) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    // ─── Deferred-login path ───
    // If the visitor hasn't authed yet, we can't hit /api/upload (auth-guarded).
    // Instead we hold the File objects locally and generate blob URLs so the
    // user still sees photo previews. They'll be uploaded to the server the
    // moment auth succeeds inside AuthGateModal.
    if (!user) {
      const additions = ok.map((f) => ({
        file: f,
        previewUrl: URL.createObjectURL(f),
        key: `pending-${f.name}-${f.size}-${f.lastModified}-${Math.random().toString(36).slice(2, 7)}`,
      }));
      setPendingFiles((prev) => [...prev, ...additions]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast.success(`${ok.length} photo${ok.length > 1 ? "s" : ""} attached — will upload on Publish`);
      return;
    }

    // ─── Authed path (server upload) ───
    setBusy(true);
    let successCount = 0;
    let failCount = 0;
    for (let i = 0; i < ok.length; i++) {
      setUploadProgress(`${i + 1}/${ok.length}`);
      const fd = new FormData();
      fd.append("file", ok[i]);
      try {
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        // Functional update — never reads stale closure state, safe even if
        // multiple uploads race or React re-renders mid-loop.
        setForm((f) => ({ ...f, image_paths: [...f.image_paths, data.path] }));
        successCount++;
      } catch (err) {
        failCount++;
        // eslint-disable-next-line no-console
        console.error(`Upload failed for ${ok[i].name}:`, err);
      }
    }
    setUploadProgress(null);
    setBusy(false);
    // CRITICAL: reset the input's value so the user can re-select the same
    // files (or additional files) without needing to reload the page.
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (successCount > 0) toast.success(`${successCount} photo${successCount > 1 ? "s" : ""} uploaded`);
    if (failCount > 0) toast.error(`${failCount} photo${failCount > 1 ? "s" : ""} failed — please retry`);
  };

  const removeImage = (p) => set("image_paths", form.image_paths.filter((x) => x !== p));

  const [videoBusy, setVideoBusy] = useState(false);
  const [videoPhase, setVideoPhase] = useState(null); // 'compress' | 'upload'
  const [videoPct, setVideoPct] = useState(0);

  const uploadVideo = async (e) => {
    const picked = e.target.files?.[0];
    e.target.value = "";
    if (!picked) return;
    let file = picked;

    setVideoBusy(true);
    setVideoPct(0);

    // Try to shrink large clips in-browser first (fast upload on mobile data).
    // Only for files big enough to be worth it; always fall back to original.
    if (picked.size > 12 * 1024 * 1024) {
      try {
        setVideoPhase("compress");
        const smaller = await compressVideo(picked, (p) => setVideoPct(p));
        if (smaller && smaller.size < picked.size) file = smaller;
      } catch (err) {
        console.warn("Video compression unavailable, uploading original", err);
      }
    }

    if (file.size > 60 * 1024 * 1024) {
      setVideoBusy(false); setVideoPhase(null);
      toast.error("Video is too large even after optimising (max 60MB). Please upload a shorter clip.");
      return;
    }

    try {
      setVideoPhase("upload");
      setVideoPct(0);
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => {
          if (ev.total) setVideoPct(Math.round((ev.loaded / ev.total) * 100));
        },
      });
      set("video_url", IMG(data.path)); // full served URL → plays inline with Range support
      toast.success("Video uploaded");
    } catch (err) {
      toast.error(formatApiError(err) || "Video upload failed");
    } finally {
      setVideoBusy(false); setVideoPhase(null); setVideoPct(0);
    }
  };

  const removePendingFile = (key) => {
    setPendingFiles((prev) => {
      const target = prev.find((p) => p.key === key);
      if (target) {
        try { URL.revokeObjectURL(target.previewUrl); } catch (_) { /* noop */ }
      }
      return prev.filter((p) => p.key !== key);
    });
  };

  // Revoke all blob URLs when the component unmounts so we don't leak memory.
  useEffect(() => {
    return () => {
      pendingFiles.forEach((p) => {
        try { URL.revokeObjectURL(p.previewUrl); } catch (_) { /* noop */ }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * uploadPendingFiles — after auth, drain the pendingFiles queue by pushing
   * each File to /api/upload and folding the resulting storage paths into
   * form.image_paths. Returns the final list of image paths so the caller
   * can POST /properties with the full set (avoids the setState race we'd
   * otherwise have if we relied on `form.image_paths` right after setForm).
   */
  const uploadPendingFiles = async () => {
    if (!pendingFiles.length) return form.image_paths;
    const newPaths = [];
    for (let i = 0; i < pendingFiles.length; i++) {
      setUploadProgress(`${i + 1}/${pendingFiles.length}`);
      const fd = new FormData();
      fd.append("file", pendingFiles[i].file);
      try {
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newPaths.push(data.path);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Post-auth upload failed:", err);
      }
    }
    setUploadProgress(null);
    // Cleanup blob previews and reset state
    pendingFiles.forEach((p) => {
      try { URL.revokeObjectURL(p.previewUrl); } catch (_) { /* noop */ }
    });
    setPendingFiles([]);
    const merged = [...form.image_paths, ...newPaths];
    setForm((f) => ({ ...f, image_paths: [...f.image_paths, ...newPaths] }));
    return merged;
  };

  /**
   * commit — the actual property write. Assumes the caller has verified auth.
   * Handles pending file uploads first, then POST/PUT /properties.
   */
  const commit = async () => {
    setBusy(true);
    try {
      // If we've got queued client-side files (came from the deferred-login
      // path), push them to /api/upload NOW that we have an auth token.
      let mergedPaths = form.image_paths;
      if (pendingFiles.length > 0) {
        mergedPaths = await uploadPendingFiles();
      }

      const payload = {
        ...form,
        image_paths: mergedPaths,
        bhk: +form.bhk,
        bathrooms: +form.bathrooms,
        area_sqft: +form.area_sqft,
        price: +form.price,
      };
      const { data } = isEdit
        ? await api.put(`/properties/${id}`, payload)
        : await api.post("/properties", payload);

      // GA4 event via GTM dataLayer — routed to Google Analytics from the
      // `GTM-WF975SGR` workspace. Slice-and-diceable by listing_type / city /
      // price / bhk. Fires only after backend confirms the write succeeded.
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: isEdit ? "property_listing_updated" : "property_listing_submitted",
          property_id: data.id,
          listing_type: payload.listing_type,   // "rent" | "sale"
          property_type: payload.property_type, // "apartment" | "house" | ...
          city: payload.city,
          locality: payload.locality,
          price: payload.price,
          currency: "INR",
          bhk: payload.bhk,
          area_sqft: payload.area_sqft,
          moderation_status: data.moderation_status || "pending",
          is_edit: isEdit,
        });
      } catch (_) { /* dataLayer might not be ready — silent no-op */ }

      // Google Ads lead conversion — new owner listing published.
      if (!isEdit) fireLeadConversion();

      if (!isEdit && data.moderation_status === "pending") {
        toast.success("Listing submitted — pending admin approval");
      } else {
        toast.success(isEdit ? "Listing updated!" : "Property listed!");
      }
      if (!isEdit) {
        // First: AI review of the freshly-listed property (owner keeps control),
        // then the Boost upsell once they resolve the review.
        setNormalizeTarget({ id: data.id, title: data.title });
      } else {
        nav(`/property/${data.id}`);
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  /**
   * submit — button handler. Enforces the deferred-login gate:
   *   • unauthed → fire GTM intent event, open AuthGateModal, mark pending.
   *   • authed   → run commit() directly.
   *
   * When the AuthGateModal succeeds, `user` transitions from false → object
   * which triggers the useEffect below to drain the pendingSubmit flag and
   * call commit(). We do NOT call commit() from the modal's onSuccess to
   * avoid stale-closure bugs (that submit closure still saw user=false).
   */
  const submit = async () => {
    if (!isEdit && !user) {
      // GTM: user clicked Publish while unauthed — a strong intent signal
      // distinct from the earlier `post_listing_clicked` (which fires from
      // the /owners landing CTA). Useful for measuring drop-off between
      // "started form" and "clicked publish".
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "post_listing_publish_clicked",
          authenticated: false,
          pending_photo_count: pendingFiles.length,
          city: form.city,
          listing_type: form.listing_type,
        });
      } catch (_) { /* noop */ }
      setPendingSubmit(true);
      setAuthGateOpen(true);
      return;
    }
    await commit();
  };

  // Auth just completed while the user was mid-flow. Drain the flag and
  // finish the publish they initiated.
  useEffect(() => {
    if (pendingSubmit && user) {
      setPendingSubmit(false);
      commit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, pendingSubmit]);

  const canNext = () => {
    if (step === 0) return form.title && form.description && form.price > 0;
    if (step === 1) return form.city && form.locality && form.address;
    if (step === 2) return form.bhk && form.bathrooms && form.area_sqft;
    return true;
  };

  if (loading) return <div className="p-20 text-center text-gray-500">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <p className="label-eyebrow text-gray-500 mb-2">{isEdit ? "Edit listing" : "List your property"}</p>
      <h1 className="font-display text-4xl sm:text-5xl brand-forest mb-8">
        {isEdit ? "Update your listing" : "Publish a new listing"}
      </h1>

      <div className="flex items-center gap-2 mb-10">
        {STEPS.map((s, i) => (
          <div key={s} className="flex-1 flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
              i < step ? "bg-[#FF5A3D] text-white" : i === step ? "bg-[#0EA5E9] text-white" : "bg-[#F0E4D2] text-gray-500"
            }`}>{i < step ? <Check size={14} /> : i + 1}</div>
            <span className={`text-sm ${i === step ? "font-semibold brand-forest" : "text-gray-500"}`}>{s}</span>
            {i < STEPS.length - 1 && <div className="flex-1 h-px bg-[#F0E4D2]" />}
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-brand-subtle p-8 space-y-5">
        {step === 0 && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.listing_type} onValueChange={(v) => set("listing_type", v)}>
                <SelectTrigger data-testid="new-listing-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rent">For Rent</SelectItem>
                  <SelectItem value="sale">For Sale</SelectItem>
                </SelectContent>
              </Select>
              <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
                <SelectTrigger data-testid="new-property-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="apartment">Apartment</SelectItem>
                  <SelectItem value="villa">Villa</SelectItem>
                  <SelectItem value="house">Independent House</SelectItem>
                  <SelectItem value="pg">PG / Roommates</SelectItem>
                  <SelectItem value="plot">Plot / Land</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-semibold mb-1 block">Title</label>
              <Input data-testid="new-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Spacious 2BHK near metro" />
            </div>
            <div>
              <label className="text-sm font-semibold mb-1 block">Description</label>
              <Textarea data-testid="new-description" rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Describe your property..." />
            </div>
            <div>
              <label className="text-sm font-semibold mb-1 block">Price (₹{form.listing_type === "rent" ? " per month" : ""})</label>
              <Input data-testid="new-price" type="number" value={form.price} onChange={(e) => set("price", e.target.value)} />
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold mb-1 block">City</label>
                <Input data-testid="new-city" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Bengaluru" />
              </div>
              <div>
                <label className="text-sm font-semibold mb-1 block">Locality</label>
                <Input data-testid="new-locality" value={form.locality} onChange={(e) => set("locality", e.target.value)} placeholder="Koramangala" />
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold mb-1 block">Full address</label>
              <Textarea data-testid="new-address" rows={3} value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Street, building name, landmark" />
            </div>
            <div>
              <label className="text-sm font-semibold mb-1 block">Available from (optional)</label>
              <Input data-testid="new-avail" type="date" value={form.available_from} onChange={(e) => set("available_from", e.target.value)} />
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-sm font-semibold mb-1 block">BHK</label>
                <Input data-testid="new-bhk" type="number" min={1} value={form.bhk} onChange={(e) => set("bhk", e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-semibold mb-1 block">Bathrooms</label>
                <Input data-testid="new-bath" type="number" min={1} value={form.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-semibold mb-1 block">Area (sqft)</label>
                <Input data-testid="new-area" type="number" min={1} value={form.area_sqft} onChange={(e) => set("area_sqft", e.target.value)} />
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold mb-1 block">Furnishing</label>
              <Select value={form.furnishing} onValueChange={(v) => set("furnishing", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unfurnished">Unfurnished</SelectItem>
                  <SelectItem value="semi">Semi-furnished</SelectItem>
                  <SelectItem value="fully">Fully furnished</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-semibold mb-2 block">Amenities</label>
              <div className="flex flex-wrap gap-2">
                {AMENITIES.map((a) => (
                  <button
                    key={a}
                    type="button"
                    data-testid={`amenity-${a}`}
                    onClick={() => toggleAmenity(a)}
                    className={`px-4 py-2 rounded-full text-sm capitalize border transition-colors ${
                      form.amenities.includes(a)
                        ? "bg-[#FF5A3D] text-white border-[#FF5A3D]"
                        : "bg-white text-gray-700 border-brand-subtle hover:border-[#FF5A3D]"
                    }`}
                  >{a}</button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold mb-1 block">Visiting hours (for AI-booked visits)</label>
              <p className="text-xs text-gray-500 mb-2">Pick the days & window when tenants can visit. Our assistant offers these slots and notifies you on WhatsApp when someone books.</p>
              <VisitingHoursEditor value={form.visiting_windows} onChange={(v) => set("visiting_windows", v)} />
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <label className="block border-2 border-dashed border-brand-subtle rounded-2xl p-10 text-center cursor-pointer hover:border-[#FF5A3D]" data-testid="upload-area">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={upload}
                disabled={busy}
                className="hidden"
              />
              <Upload size={28} className="mx-auto brand-forest mb-3" />
              <p className="font-semibold brand-forest">
                {uploadProgress ? `Uploading ${uploadProgress}…` : "Click to upload property photos"}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {uploadProgress
                  ? "Please keep this page open until done"
                  : "Select MULTIPLE photos at once · JPG, PNG, WebP · up to 8MB each"}
              </p>
            </label>
            {(form.image_paths.length > 0 || pendingFiles.length > 0) && (
              <div className="grid grid-cols-3 gap-3">
                {form.image_paths.map((p) => (
                  <div key={p} className="relative rounded-xl overflow-hidden group">
                    <img src={IMG(p)} alt="" className="w-full h-32 object-cover" />
                    <button onClick={() => removeImage(p)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <X size={14} />
                    </button>
                  </div>
                ))}
                {pendingFiles.map((pf) => (
                  <div key={pf.key} className="relative rounded-xl overflow-hidden group ring-2 ring-[#C9A24A]/50">
                    <img src={pf.previewUrl} alt="" className="w-full h-32 object-cover" />
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-[#C9A24A] text-white text-[10px] font-bold uppercase tracking-wider">
                      Pending
                    </div>
                    <button onClick={() => removePendingFile(pf.key)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Video walkthrough — upload MP4 from phone (primary) or paste a link */}
            <div className="pt-2">
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Video walkthrough <span className="text-gray-400 font-normal">(optional)</span>
              </label>

              {form.video_url ? (
                <div className="rounded-2xl border border-brand-subtle overflow-hidden" data-testid="video-preview">
                  {/\.(mp4|webm|ogg|mov|m4v|3gp)$/i.test(form.video_url) ? (
                    <video src={form.video_url} controls playsInline preload="metadata" className="w-full max-h-72 bg-black" />
                  ) : (
                    <div className="p-4 text-sm text-gray-600 break-all">{form.video_url}</div>
                  )}
                  <button type="button" onClick={() => set("video_url", "")} data-testid="video-remove"
                    className="w-full py-2 text-sm font-semibold text-[#7A1E2A] hover:bg-[#FBF6EC] border-t border-brand-subtle flex items-center justify-center gap-1.5">
                    <X size={14} /> Remove video
                  </button>
                </div>
              ) : (
                <>
                  {videoBusy ? (
                    <div className="rounded-2xl border border-brand-subtle p-5" data-testid="video-upload-progress">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold brand-forest">
                          {videoPhase === "compress" ? "Optimising your video…" : "Uploading video…"}
                        </p>
                        <span className="text-sm font-bold brand-forest tabular-nums" data-testid="video-progress-pct">{videoPct}%</span>
                      </div>
                      <div className="h-2.5 w-full rounded-full bg-[#F0E7D6] overflow-hidden">
                        <div className="h-full rounded-full bg-[#C9A24A] transition-all duration-200" style={{ width: `${videoPct}%` }} />
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {videoPhase === "compress"
                          ? "Shrinking it so it uploads fast on mobile data — keep this page open."
                          : "Almost there — keep this page open until it finishes."}
                      </p>
                    </div>
                  ) : (
                    <label className="block border-2 border-dashed border-brand-subtle rounded-2xl p-6 text-center cursor-pointer hover:border-[#C9A24A]" data-testid="video-upload-area">
                      <input type="file" accept="video/mp4,video/quicktime,video/webm,video/*" onChange={uploadVideo} disabled={videoBusy} className="hidden" />
                      <Upload size={24} className="mx-auto brand-forest mb-2" />
                      <p className="font-semibold brand-forest text-sm">Upload an MP4 from your phone</p>
                      <p className="text-xs text-gray-500 mt-1">Record a 30–90 sec walkthrough · large clips are auto-shrunk · MP4/MOV</p>
                    </label>
                  )}
                  <div className="mt-3">
                    <Input
                      data-testid="video-url"
                      value={form.video_url || ""}
                      onChange={(e) => set("video_url", e.target.value)}
                      placeholder="…or paste a YouTube / Instagram Reel / MP4 link"
                      className="h-11"
                      disabled={videoBusy}
                    />
                  </div>
                </>
              )}
            </div>
          </>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-brand-subtle">
          <Button variant="ghost" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="rounded-full">
            <ArrowLeft size={16} className="mr-1" /> Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button data-testid="next-step" onClick={() => setStep(step + 1)} disabled={!canNext()} className="bg-brand-forest hover:bg-[#E64520] text-white rounded-full h-11 px-6">
              Next <ArrowRight size={16} className="ml-1" />
            </Button>
          ) : (
            <Button data-testid="publish-property" onClick={submit} disabled={busy} className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white rounded-full h-11 px-6">
              {busy
                ? (uploadProgress ? `Uploading ${uploadProgress}…` : "Saving…")
                : isEdit
                  ? "Save changes"
                  : (!user ? (<><Lock size={14} className="mr-1.5" /> Sign in & Publish</>) : "Publish listing")}
            </Button>
          )}
        </div>
      </div>

      <AuthGateModal
        open={authGateOpen}
        onOpenChange={setAuthGateOpen}
        onSuccess={() => {
          // No-op: the pendingSubmit useEffect above fires commit() once the
          // AuthProvider updates `user`. Prevents stale-closure double-submit.
          //
          // We intentionally do NOT clear pendingSubmit when the modal closes
          // (previous impl had a race — the modal calls onOpenChange(false)
          // synchronously before onSuccess, which caused the effect to see
          // pendingSubmit=false right when user flipped truthy → no commit).
          // If the user cancels the modal without authing, pendingSubmit
          // stays true but is harmless: they can only trigger commit by
          // logging in, which is exactly what we want.
        }}
      />

      <NormalizeReviewModal
        open={!!normalizeTarget}
        propertyId={normalizeTarget?.id}
        onResolved={() => {
          const t = normalizeTarget;
          setNormalizeTarget(null);
          if (t) setUpsell({ id: t.id, title: t.title });
        }}
      />

      <BoostUpsellModal
        open={!!upsell}
        propertyId={upsell?.id}
        propertyTitle={upsell?.title}
        onClose={() => setUpsell(null)}
      />
    </div>
  );
}


const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HOURS = Array.from({ length: 15 }, (_, i) => 7 + i); // 7:00 .. 21:00
const hlabel = (h) => `${((h % 12) || 12)}:00 ${h < 12 ? "AM" : "PM"}`;

function VisitingHoursEditor({ value = [], onChange }) {
  const [days, setDays] = useState(() => (value.length ? [...new Set(value.map((w) => w.day))] : ["Sat", "Sun"]));
  const [from, setFrom] = useState(() => value[0]?.from || "10:00");
  const [to, setTo] = useState(() => value[0]?.to || "18:00");

  const emit = (d, f, t) => onChange(d.map((day) => ({ day, from: f, to: t })));
  const toggleDay = (day) => {
    const next = days.includes(day) ? days.filter((x) => x !== day) : [...days, day];
    setDays(next); emit(next, from, to);
  };

  return (
    <div className="rounded-xl border border-brand-subtle p-3" data-testid="visiting-hours-editor">
      <div className="flex flex-wrap gap-2 mb-3">
        {DAYS.map((d) => (
          <button key={d} type="button" onClick={() => toggleDay(d)} data-testid={`vh-day-${d}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              days.includes(d) ? "bg-[#7A1E2A] text-white border-[#7A1E2A]" : "bg-white text-gray-600 border-brand-subtle hover:border-[#C9A24A]"}`}>{d}</button>
        ))}
      </div>
      <div className="flex items-center gap-2 text-sm">
        <span className="text-gray-500">From</span>
        <select value={from} onChange={(e) => { setFrom(e.target.value); emit(days, e.target.value, to); }}
          data-testid="vh-from" className="rounded-lg border border-brand-subtle px-2 py-1.5 bg-white">
          {HOURS.map((h) => <option key={h} value={`${String(h).padStart(2, "0")}:00`}>{hlabel(h)}</option>)}
        </select>
        <span className="text-gray-500">to</span>
        <select value={to} onChange={(e) => { setTo(e.target.value); emit(days, from, e.target.value); }}
          data-testid="vh-to" className="rounded-lg border border-brand-subtle px-2 py-1.5 bg-white">
          {HOURS.map((h) => <option key={h} value={`${String(h).padStart(2, "0")}:00`}>{hlabel(h)}</option>)}
        </select>
      </div>
    </div>
  );
}
